// Picker API Service
import apiClient from './index';
import axios from 'axios';
import { API_BASE_URL, getToken } from './config';

/**
 * Get picker dashboard data
 * @returns {Promise} API response with dashboard stats
 */
export const getPickerDashboard = async () => {
  try {
    const response = await apiClient.get('/picker/dashboard');

    // Handle API response format
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Dashboard data retrieved successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to retrieve dashboard data',
      data: null,
    };
  } catch (error) {
    console.error('Error fetching picker dashboard:', error);
    return {
      success: false,
      message: error.message || 'Failed to retrieve dashboard data',
      error: error.error || error.message,
      data: null,
    };
  }
};

/**
 * Get picker orders
 * @returns {Promise} API response with orders data
 */
export const getPickerOrders = async () => {
  try {
    const response = await apiClient.get('/picker/orders');

    // Handle API response format
    if (response.success && response.data && Array.isArray(response.data)) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Orders retrieved successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to retrieve orders',
      data: [],
    };
  } catch (error) {
    console.error('Error fetching picker orders:', error);
    return {
      success: false,
      message: error.message || 'Failed to retrieve orders',
      error: error.error || error.message,
      data: [],
    };
  }
};

/**
 * Update order product with actual quantity and price
 * @param {number} orderProductId - Order product ID
 * @param {number} actualQuantity - Actual quantity collected
 * @param {number} actualPrice - Actual price (actualQuantity * rate)
 * @param {number} customerRate - Optional customer/selling rate
 * @returns {Promise} API response
 */
export const updateOrderProduct = async (orderProductId, actualQuantity, actualPrice, customerRate = null) => {
  try {
    const payload = {
      actual_quantity: actualQuantity,
      actual_price: actualPrice
    };

    // Include customer_rate if provided
    if (customerRate != null && !isNaN(customerRate)) {
      payload.customer_rate = parseFloat(customerRate);
    }

    const response = await apiClient.put(`/picker/order-products/${orderProductId}`, payload);

    if (response.success !== false) {
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Order product updated successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to update order product',
    };
  } catch (error) {
    console.error('Error updating order product:', error);
    return {
      success: false,
      message: error.message || 'Failed to update order product',
      error: error.error || error.message,
    };
  }
};

/**
 * Update order status and summary
 * @param {number} orderId - Order ID
 * @param {string} status - Order status (PAID, REJECTED, etc.)
 * @param {string} summary - Order summary/reason (optional)
 * @returns {Promise} API response
 */
export const updateOrder = async (orderId, status, summary = null) => {
  try {
    const updateData = { order_status: status };
    if (summary) {
      updateData.summary = summary;
    }

    const response = await apiClient.put(`/picker/orders/${orderId}`, updateData);

    if (response.success !== false) {
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Order updated successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to update order',
    };
  } catch (error) {
    console.error('Error updating order:', error);
    return {
      success: false,
      message: error.message || 'Failed to update order',
      error: error.error || error.message,
    };
  }
};

/**
 * Get picker order by ID
 * @param {number} orderId - Order ID
 * @returns {Promise} API response with order data
 */
export const getPickerOrderById = async (orderId) => {
  try {
    const response = await apiClient.get(`/picker/orders/${orderId}`);

    if (response.success !== false && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Order retrieved successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to retrieve order',
    };
  } catch (error) {
    console.error('Error fetching picker order:', error);
    return {
      success: false,
      message: error.message || 'Failed to retrieve order',
      error: error.error || error.message,
    };
  }
};

/**
 * Get assigned orders for picker with optional filter
 * @param {string} filter - Filter type: 'all', 'today', 'week', 'month'
 * @returns {Promise} API response with orders and summary
 */
export const getAssignedOrders = async (filter = 'all') => {
  try {
    const params = filter && filter !== 'all' ? `?filter=${filter}` : '';
    const response = await apiClient.get(`/picker/assigned-orders${params}`);

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Assigned orders retrieved successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to retrieve assigned orders',
      data: null,
    };
  } catch (error) {
    console.error('Error fetching assigned orders:', error);
    return {
      success: false,
      message: error.message || 'Failed to retrieve assigned orders',
      error: error.error || error.message,
      data: null,
    };
  }
};

/**
 * Get products list
 * @returns {Promise} API response with products data
 */
export const getProducts = async () => {
  try {
    const response = await apiClient.get('/products');

    // API returns: { success: true, message: "...", data: [...] }
    if (response.success === true && response.data && Array.isArray(response.data)) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Products retrieved successfully',
      };
    }

    // Handle case where response is directly an array
    if (Array.isArray(response)) {
      return {
        success: true,
        data: response,
        message: 'Products retrieved successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to retrieve products',
      data: [],
    };
  } catch (error) {
    console.error('Error fetching products:', error);
    return {
      success: false,
      message: error.message || 'Failed to retrieve products',
      error: error.error || error.message,
      data: [],
    };
  }
};

/**
 * Upload image for order
 * @param {number} orderId - Order ID
 * @param {File} imageFile - Image file to upload
 * @returns {Promise} API response with image URL
 */
export const uploadOrderImage = async (orderId, imageFile) => {
  try {
    const formData = new FormData();
    formData.append('image', imageFile);

    const token = getToken();
    
    const response = await axios.post(`${API_BASE_URL}/orders/${orderId}/upload-image`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    // Handle API response - expect URL in response
    if (response.data) {
      const imageUrl = response.data.url || response.data.image_url || response.data.data?.url || response.data.data;
      
      return {
        success: true,
        imageUrl: imageUrl,
        message: response.data.message || 'Image uploaded successfully',
      };
    }

    return {
      success: false,
      message: response.data?.message || 'Failed to upload image',
    };
  } catch (error) {
    console.error('Error uploading order image:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to upload image',
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Add products to order
 * @param {number} orderId - Order ID
 * @param {Array} products - Array of product objects
 * @returns {Promise} API response
 */
export const addProductsToOrder = async (orderId, products) => {
  try {
    const response = await apiClient.post(`/picker/orders/${orderId}/add-products`, {
      products: products
    });

    if (response.success !== false) {
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Products added successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to add products',
    };
  } catch (error) {
    console.error('Error adding products to order:', error);
    return {
      success: false,
      message: error.message || 'Failed to add products',
      error: error.error || error.message,
    };
  }
};

/**
 * Generate OTP for order
 * @param {number} orderId - Order ID
 * @returns {Promise} API response
 */
export const generateOtp = async (orderId) => {
  try {
    const response = await apiClient.post(`/picker/orders/${orderId}/generate-otp`);

    if (response.success !== false) {
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'OTP generated successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Failed to generate OTP',
    };
  } catch (error) {
    console.error('Error generating OTP:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to generate OTP',
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Verify OTP for order
 * @param {number} orderId - Order ID
 * @param {number} otp - OTP code to verify
 * @returns {Promise} API response
 */
export const verifyOtp = async (orderId, otp) => {
  try {
    const response = await apiClient.post(`/picker/orders/${orderId}/verify-otp`, {
      otp: parseInt(otp, 10)
    });

    if (response.success !== false) {
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'OTP verified successfully',
      };
    }

    return {
      success: false,
      message: response.message || 'Invalid OTP. Please try again.',
    };
  } catch (error) {
    console.error('Error verifying OTP:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Invalid OTP. Please try again.',
      error: error.response?.data?.error || error.message,
    };
  }
};


